import {
  type Header,
  type HeaderGroup,
  type Row,
  flexRender,
} from "@tanstack/react-table";
import { type PropsWithChildren, useRef } from "react";

import { DataTablePagination } from "@/components/shared/tables/DataTablePagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useTranslation } from "react-i18next";
import { useExportsTableInstanceContext } from "./table-instance-provider";

export const ExportsTable = () => {
  const tableRef = useRef(null);
  const { t } = useTranslation();

  const { table } = useExportsTableInstanceContext();

  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-md border bg-background">
        <div className="flex flex-1">
          <Table ref={tableRef}>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup: HeaderGroup<any>) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header: Header<any, any>) => {
                    return (
                      <TableHead
                        key={header.id}
                        colSpan={header.colSpan}
                        style={{
                          width: `${header.getSize()}px`,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table?.getRowModel().rows.length ? (
                table?.getRowModel().rows.map((row: Row<any>) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell: any) => {
                      const hasMeta =
                        cell.getContext().cell.column.columnDef.meta;
                      const style = hasMeta?.getCellContext(
                        cell.getContext(),
                      )?.style;

                      return (
                        <TableCell
                          key={cell.id}
                          style={style}
                          align={
                            hasMeta?.getCellContext(cell.getContext())?.align
                          }
                          className="py-2 truncate-short"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={table.getHeaderGroups()[0].headers.length}
                  >
                    <div className="p-4 text-center">
                      {t("exports.table.empty")}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <DataTablePagination table={table} />
    </div>
  );
};
